import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaRegPauseCircle, FaRegPlayCircle } from 'react-icons/fa'; 
import { useTranslation } from 'react-i18next'; 
import recurrenceOptions from '../../data/recurrenceOptions'; 
import statusOptions from '../../data/status'; 
import i18n from '../../i18n'; // Import i18n instance
import '../../style/ConditionPage.css'; // Import the CSS file

const ConditionPage = () => {
    const { id, conditionId } = useParams(); // Get pet ID and condition ID from URL
    const { t } = useTranslation();
    const navigate = useNavigate(); // For redirection after deletion
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const [condition, setCondition] = useState(null);
    const [error, setError] = useState(null);
    const [showAddTreatmentForm, setShowAddTreatmentForm] = useState(false); // Toggle for treatment form
    const [newTreatment, setNewTreatment] = useState({
        name: '',
        lastGivenDate: '',
        recurrence: '',
        endDate: '',
    });
    const [expandedTreatmentId, setExpandedTreatmentId] = useState(null); // Track expanded treatment
    const [showRecoveryForm, setShowRecoveryForm] = useState(false); // Track if recovery form is shown
    const [recoveryDate, setRecoveryDate] = useState(''); // Track recovery date
    const [loading, setLoading] = useState(false); // Track loading state
    const [showGivenDateInput, setShowGivenDateInput] = useState(null); // Track which treatment is showing the input
    const [givenDate, setGivenDate] = useState('');

    useEffect(() => {
        const fetchConditionDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error('No authentication token found');
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch condition details');
                }

                const data = await response.json();
                console.log('Fetched Condition Data:', data); // Debug log
                setCondition(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchConditionDetails();
    }, [id, conditionId, baseUrl]);

    const getRecurrenceLabel = (value) => {
        const option = recurrenceOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const getStatusLabel = (value) => {
        const option = statusOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const handleConfirmRecovery = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                method: 'PUT', // Use PUT to update recovery status and date
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ recovered: true, recoveryDate }), // Update recovery status and date
            });

            if (!response.ok) {
                throw new Error('Failed to update recovery status');
            }

            const updatedCondition = await response.json();
            console.log('Updated Condition:', updatedCondition); // Debug log

            // Replace the entire condition state with the updated condition
            setCondition(updatedCondition);
            setShowRecoveryForm(false); // Hide the recovery form
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    const handleCancelRecovery = () => {
        setShowRecoveryForm(false); // Hide the recovery form
        setRecoveryDate(''); // Reset the recovery date
    };

    const handleEditClick = () => {
        navigate(`/pet/${id}/conditions/${conditionId}/edit`);
    };



    const handleAddTreatment = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(newTreatment),
            });

            if (!response.ok) {
                throw new Error('Failed to add treatment');
            }

            // Fetch the updated condition data after adding the treatment
            const updatedConditionResponse = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!updatedConditionResponse.ok) {
                throw new Error('Failed to fetch updated condition details');
            }

            const updatedCondition = await updatedConditionResponse.json();
            setCondition(updatedCondition); // Update the state with the latest condition data

            // Reset the treatment form
            setNewTreatment({
                name: '',
                lastGivenDate: '',
                recurrence: '',
                endDate: '',
            });
            setShowAddTreatmentForm(false); // Hide the form after adding
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    const handleEditTreatmentClick = (treatmentId) => {
        navigate(`/pet/${id}/conditions/${conditionId}/treatments/${treatmentId}/edit`);
    };

    const toggleTreatmentDetails = (treatmentId) => {
        setExpandedTreatmentId((prevId) => (prevId === treatmentId ? null : treatmentId)); // Toggle only the selected treatment
    };

    const handleToggleTreatmentStatus = async (treatmentId) => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;
    
            if (!token) {
                throw new Error('No authentication token found');
            }
    
            const treatment = condition.treatments.find((t) => t.id === treatmentId || t._id === treatmentId);
            const newStatus = treatment.status === 'active' ? 'pause' : 'activate';
    
            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments/${treatmentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: newStatus }),
            });
    
            if (!response.ok) {
                throw new Error('Failed to update treatment status');
            }
    
            const updatedCondition = await response.json();
            setCondition(updatedCondition); // Update the condition state
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleShowGivenDateInput = (treatmentId) => {
        setShowGivenDateInput(treatmentId); // Show the input for the selected treatment
        setGivenDate(''); // Reset the given date
    };

    const handleCancelGivenDateInput = () => {
        setShowGivenDateInput(null); // Hide the input
        setGivenDate(''); // Reset the given date
    };

    const handleConfirmGivenDate = async (treatmentId) => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/conditions/${conditionId}/treatments/${treatmentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: 'given', date: givenDate }), // Send the given date
            });

            if (!response.ok) {
                throw new Error('Failed to mark treatment as given');
            }

            const updatedCondition = await response.json();
            setCondition(updatedCondition); // Update the condition state
            setShowGivenDateInput(null); // Hide the input
            setGivenDate(''); // Reset the given date
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return <div className="error-message">Error: {error}</div>;
    }

    if (!condition) {
        return <div className="loading-message">{t('general.loading')}</div>;
    }

    return (
        <div className="condition-details-page">
            <div className="condition-header flex justify-between items-center mb-6">
                <div className="header-content flex items-center">
                    <h2 className="condition-title text-dafauna-darkText text-2xl font-bold capitalize">
                        {condition.type} - {condition.name}
                    </h2>
                    <FaEdit
                        className="edit-icon text-dafauna-primary text-lg cursor-pointer hover:text-dafauna-primaryLight transition-colors"
                        onClick={handleEditClick}
                        title={t('condition.titleEdit')}
                    />
                </div>
            </div>
            <p className="text-dafauna-darkText text-base mb-4">
                <strong>{t('condition.diagnosisDate')}:</strong> {new Date(condition.diagnosisDate).toLocaleDateString()}
            </p>
            <p className="text-dafauna-darkText text-base mb-4">
                <strong>{t('condition.notes')}:</strong> {condition.notes || t('condition.noNotes')}
            </p>
            <div className="condition-status flex items-center gap-4">
                <p className="text-dafauna-darkText text-base">
                    <strong>{t('condition.recovered')}:</strong> {condition.recovered ? t('general.yes') : t('general.no')}
                </p>
                {!condition.recovered && !showRecoveryForm && (
                    <p
                        onClick={() => setShowRecoveryForm(true)}
                        className="clickable-text text-dafauna-primary text-sm font-semibold cursor-pointer underline"
                    >
                        {t('condition.isRecovered')}
                    </p>
                )}
            </div>

            {condition.recovered && condition.recoveryDate && (
                <p className="text-dafauna-darkText text-base mb-4">
                    <strong>{t('condition.recoveryDate')}:</strong> {new Date(condition.recoveryDate).toLocaleDateString()}
                </p>
            )}

            {showRecoveryForm && (
                <div className="recovery-form bg-dafauna-primaryLight border border-gray-300 rounded-md p-4 mt-4">
                    <div className="date-input-container flex flex-col gap-4">
                        <h4 className="text-dafauna-darkText font-medium">{t('condition.confirmRecovery')}</h4>
                        <input
                            type="date"
                            placeholder={t('condition.recoveryDatePlaceholder')}
                            value={recoveryDate}
                            onChange={(e) => setRecoveryDate(e.target.value)}
                            className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                            required
                        />
                        <div className="date-input-actions flex gap-4">
                            <button
                                onClick={handleConfirmRecovery}
                                className="btn confirm-date-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                            >
                                {t('general.confirm')}
                            </button>
                            <button
                                onClick={handleCancelRecovery}
                                className="btn cancel-date-btn bg-gray-400 text-white rounded-md px-4 py-2 hover:bg-gray-500"
                            >
                                {t('general.cancel')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="treatments-header flex justify-between items-center mb-6">
                <h3 className="text-dafauna-darkText text-xl font-bold">{t('condition.treatments')}</h3>
                <p
                    onClick={() => setShowAddTreatmentForm((prev) => !prev)}
                    className="clickable-text-treatments text-dafauna-primary text-sm font-semibold cursor-pointer underline"
                >
                    {showAddTreatmentForm ? t('general.cancel') : t('general.add')}
                </p>
            </div>

            {showAddTreatmentForm && (
                <div className="add-treatment-form bg-white border border-gray-300 rounded-md p-4 mt-4">
                    <div className="form-group mb-4">
                        <label htmlFor="treatment-name" className="block text-dafauna-darkText font-medium mb-2">
                            {t('condition.treatmentName')}
                        </label>
                        <input
                            id="treatment-name"
                            type="text"
                            placeholder={t('condition.treatmentNamePlaceholder')}
                            value={newTreatment.name}
                            onChange={(e) => setNewTreatment({ ...newTreatment, name: e.target.value })}
                            required
                            className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label htmlFor="last-given-date" className="block text-dafauna-darkText font-medium mb-2">
                            {t('condition.lastGivenDate')}
                        </label>
                        <input
                            id="last-given-date"
                            type="date"
                            placeholder={t('condition.lastGivenDatePlaceholder')}
                            value={newTreatment.lastGivenDate}
                            onChange={(e) => setNewTreatment({ ...newTreatment, lastGivenDate: e.target.value })}
                            required
                            className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">
                            {t('condition.recurrence')}
                        </label>
                        <select
                            id="recurrence"
                            value={newTreatment.recurrence}
                            onChange={(e) => setNewTreatment({ ...newTreatment, recurrence: e.target.value })}
                            required
                            className="form-select w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        >
                            <option value="">{t('condition.selectRecurrence')}</option>
                            {recurrenceOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label[i18n.language]}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group mb-4">
                        <label htmlFor="end-date" className="block text-dafauna-darkText font-medium mb-2">
                            {t('condition.endDate')}
                        </label>
                        <input
                            id="end-date"
                            type="date"
                            placeholder={t('condition.endDatePlaceholder')}
                            value={newTreatment.endDate}
                            onChange={(e) => setNewTreatment({ ...newTreatment, endDate: e.target.value })}
                            className="form-input w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                        />
                    </div>
                    <button
                        onClick={handleAddTreatment}
                        className="btn save-treatment-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                    >
                        {t('general.add')}
                    </button>
                </div>
            )}
            {condition.treatments && condition.treatments.length > 0 ? (
                <ul className="treatment-list space-y-4">
                    {condition.treatments.map((treatment) => (
                        <li key={treatment.id || treatment._id} className="treatment-item border border-gray-300 rounded-md p-4">
                            <div className="treatment-status flex justify-between items-center">
                                <h4 className="text-dafauna-darkText font-bold">{treatment.name}</h4>
                                <p
                                    onClick={() => toggleTreatmentDetails(treatment.id || treatment._id)}
                                    className="clickable-text-treatments-toggle text-dafauna-primary text-sm font-semibold cursor-pointer underline"
                                >
                                    {expandedTreatmentId === (treatment.id || treatment._id)
                                        ? t('general.hide')
                                        : t('general.view')}
                                </p>
                            </div>

                            {expandedTreatmentId === (treatment.id || treatment._id) && (
                                <div className="treatment-details mt-4">
                                    <div className="treatment-info space-y-2">
                                        <FaEdit
                                            className="edit-icon text-dafauna-primary text-lg cursor-pointer hover:text-dafauna-primaryLight transition-colors"
                                            onClick={() => handleEditTreatmentClick(treatment.id || treatment._id)}
                                            title={t('condition.titleEditTreatment')}
                                        />
                                        <div className="treatment-status-container flex items-center gap-4">
                                            <p className="text-dafauna-darkText text-base">
                                                <strong>{t('general.status')}:</strong> {getStatusLabel(treatment.status) || t('general.na')}
                                            </p>
                                            <div
                                                onClick={() => handleToggleTreatmentStatus(treatment.id || treatment._id)}
                                                className="status-icon cursor-pointer"
                                                title={
                                                    treatment.status === 'active'
                                                        ? t('condition.pauseTreatment')
                                                        : t('condition.activateTreatment')
                                                }
                                            >
                                                {treatment.status === 'active' ? (
                                                    <FaRegPauseCircle className="pause-icon text-dafauna-primary text-lg" />
                                                ) : (
                                                    <FaRegPlayCircle className="play-icon text-dafauna-primary text-lg" />
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-dafauna-darkText text-base">
                                            <strong>{t('general.nextOne')}:</strong>{' '}
                                            {treatment.nextGivenDate
                                                ? new Date(treatment.nextGivenDate).toLocaleDateString()
                                                : t('general.na')}
                                        </p>
                                        <p className="text-dafauna-darkText text-base">
                                            <strong>{t('general.lastOne')}:</strong>{' '}
                                            {treatment.lastGivenDate
                                                ? new Date(treatment.lastGivenDate).toLocaleDateString()
                                                : t('general.na')}
                                        </p>
                                        <p className="text-dafauna-darkText text-base">
                                            <strong>{t('condition.recurrence')}:</strong>{' '}
                                            {getRecurrenceLabel(treatment.recurrence) || t('general.na')}
                                        </p>
                                    </div>

                                    {treatment.history && treatment.history.length > 0 ? (
                                        <table className="history-table w-full border-collapse mt-4">
                                            <thead>
                                                <tr className="bg-dafauna-primary text-white">
                                                    <th className="px-4 py-2">{t('general.date')}</th>
                                                    <th className="px-4 py-2">{t('general.action')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {treatment.history
                                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                                    .map((entry, index) => (
                                                        <tr key={index} className="hover:bg-gray-100">
                                                            <td className="px-4 py-2">{new Date(entry.date).toLocaleDateString()}</td>
                                                            <td className="px-4 py-2">{entry.notes || t('general.noNotes')}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p className="text-dafauna-darkText text-sm">{t('condition.noHistory')}</p>
                                    )}

                                    <div className="actions mt-4 space-y-2">
                                        {treatment.status === 'active' && (
                                            <div>
                                                {showGivenDateInput === (treatment.id || treatment._id) ? (
                                                    <div className="date-input-container flex flex-col gap-4">
                                                        <h4 className="text-dafauna-darkText font-medium">{t('general.confirmGiven')}</h4>
                                                        <input
                                                            type="date"
                                                            value={givenDate}
                                                            onChange={(e) => setGivenDate(e.target.value)}
                                                            className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                                            required
                                                        />
                                                        <div className="date-input-actions flex gap-4">
                                                            <button
                                                                onClick={() => handleConfirmGivenDate(treatment.id || treatment._id)}
                                                                className="btn confirm-given-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                                                                disabled={loading}
                                                            >
                                                                {loading ? t('general.updating') : t('general.confirm')}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelGivenDateInput}
                                                                className="btn cancel-date-btn bg-gray-400 text-white rounded-md px-4 py-2 hover:bg-gray-500"
                                                            >
                                                                {t('general.cancel')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleShowGivenDateInput(treatment.id || treatment._id)}
                                                        className="btn mark-as-given-btn bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight"
                                                        disabled={loading}
                                                    >
                                                        {loading ? t('general.updating') : t('general.markAsGiven')}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-dafauna-darkText text-sm">{t('condition.noTreatments')}</p>
            )}
        </div>
    );
};

export default ConditionPage;